import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import {
  CreateRoleAssignmentItem,
  RoleAssignmentAttributes,
  RoleAssignmentItemStore,
  RoleAssignmentSummaryStore,
} from 'src/stores/role-assignments-store';
import { container } from 'src/inversify.config';
import { Alert, App, Checkbox, Divider, Modal, Spin } from 'antd';
import { nameof } from 'src/core/utils/object';

import RolesSelect from 'src/common/components/business/roles-select';
import ScopeHierarchySelector from 'src/common/components/business/scope-hierarchy-selector';
import { IdentityType, PermissionsConstants, ScopeType, Selector } from 'src/common/types';
import NewItemModal from 'src/core/ui/newitemmodal';
import { useIdentity } from '../../../core/services/authentication';
import { useCurrentUserPermissionsFilter } from '../../../common/permission-hooks';
import { getCurrentOrg, getSelectorFromScope } from '../../../common/utils';
import {
  GlobalLicenseManagerRoleName,
  LicenseApproverRoleName,
  LicenseManagerRoleName,
  RoleSummaryStore,
  StructmasterUserRoleName,
} from '../../../stores/roles-store';
import LicenseAttributesComponent from '../../licenses/license-attributes/license-attributes';
import {
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  NewLicensCreationAttributes,
  getUnlimitedLicenseAttributes,
} from '../../../stores/licensing-store';
import { Query } from '../../../core/stores/data-store';
import { OrganizationItemStore, ORGANIZATIONS_MEMBER_LIMIT } from 'src/stores/organizations-store';
import { Typography, Form } from 'antd';
import RoleAssignmentAttributesComponent from 'src/pages/role-assignments/role-attributes/role-attributes';
import { UserItemStore } from 'src/stores/users-store';
import moment from 'moment';

const { Text } = Typography;
interface UserRoleAssignmentFormData {
  roles: { value: string; label: string }[];
  hierarchy: { scopeId: string; scopeTitle: string; scopeType: ScopeType; parentId: string; path: string[] };
}
const perm = [PermissionsConstants.ManageGlobalLicenses, PermissionsConstants.ManageLicenses, PermissionsConstants.ManageOrganizationInfo];
const UserRoleAssignmentModal: FC<{
  userId: string;
  username: string;
  userOrganizations?: string[];
  onClose: (response?: CommandResult<CreateRoleAssignmentItem>, addAnother?: boolean) => void;
}> = (props) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const { onClose, userId, username, userOrganizations } = props;
  const [form] = Form.useForm<UserRoleAssignmentFormData>();

  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const newRoleAssignmentState = newRoleAssignmentStore.state;
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesStoreState = licenseCreationAttributesStore.state;
  const roleAssignmentSummaryStore = container.get(RoleAssignmentSummaryStore);
  const roleAssignmentSummaryState = roleAssignmentSummaryStore.state;
  const [userPermissions, checkingPermissions] = useCurrentUserPermissionsFilter(identity, getCurrentOrg(identity).id, perm);
  const hasGlobalLicenseManagerPermission = !checkingPermissions && userPermissions.has(PermissionsConstants.ManageGlobalLicenses);
  const canAddUserToOrganization = !checkingPermissions && userPermissions.has(PermissionsConstants.ManageOrganizationInfo);
  const [saveRoleWarning, setSaveRoleWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [expiredRolesWarningMessage, setExpiredRolesWarningMessage] = useState('');
  const expiredRoleAssignments = useRef<{ roleId: string; scopeId: string; assignmentId: string; label: string }[]>([]);
  const [organizationName, setOrganizationName] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<boolean>(false);
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();
  const [customError, setCustomError] = useState('');
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const [currentMaxSelector, setCurrentMaxSelector] = useState(Selector.Organization);
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [sptAbIsSelected, setIsSptAbSelected] = useState(false);
  const [roleAttributes, setRoleAttributes] = useState<RoleAssignmentAttributes>();
  const [addAnother, setAddAnother] = useState(false);
  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;
  const userStore = container.get(UserItemStore);
  const userState = userStore.state;
  const { modal } = App.useApp();

  const getOrganizationSelector = async (organizationId: string | undefined) => {
    if (organizationId) {
      await organizationStore.load(organizationId);
      return organizationState.item.isPartner.value ? Selector.Partners : Selector.Organization;
    } else {
      return Selector.Organization;
    }
  };

  const onRoleAttributesChange = (item: RoleAssignmentAttributes) => {
    setRoleAttributes(item);
  };

  const getWarningMessage = async () => {
    let item = (await form.validateFields()) as UserRoleAssignmentFormData;
    if (item) {
      var query = {
        filter: {
          identityId: { value: userId, type: 'guid' },
          identityType: IdentityType.User,
          scopeId: { value: item.hierarchy.scopeId, type: 'guid' },
        },
        skip: 0,
      } as Query;
      var selectedRoles = item.roles.map((x) => {
        return { value: x.value, label: x.label };
      });

      await roleAssignmentSummaryStore.load(query);
      var existingRoles = verifyIfUserAlreadyHasSelectedRole(
        roleAssignmentSummaryState.items.value
          .filter((x) => x.scopeId === item.hierarchy.scopeId)
          .map((x) => ({ id: x.roleId, expirationDate: x.expirationDate, scopeId: x.scopeId, assignmentId: x.id })),
        selectedRoles
      );

      const now = moment(new Date());

      if (existingRoles.length > 0 && existingRoles.some((role) => moment(role.expirationDate).isAfter(now))) {
        modal.error({
          content: t('SelectedUserAlreadyHasTheSelectedRoles').replace(
            '{0}',
            existingRoles
              .filter((v) => moment(v.expirationDate).isAfter(now))
              .map((v) => v.label)
              .join(', ')
          ),
        });
        return;
      } else {
        var roles = item.roles.filter((v) => !existingRoles.some((x) => x.id === v.value)).map((x) => x.label);
        var rolesString = roles.join(', ');

        setWarningMessage(
          roles.length > 0 ? t('AssignRoleWarning').replace('{0}', rolesString).replaceAll('{1}', username).replace('{2}', organizationName) : ''
        );
        const expiredRoles = existingRoles.filter((v) => moment(v.expirationDate).isBefore(now));
        expiredRoleAssignments.current = expiredRoles.map((v) => ({
          roleId: v.id,
          scopeId: v.scopeId,
          assignmentId: v.assignmentId,
          label: v.label,
        }));
        const expiredRoleString = expiredRoles.map((v) => v.label).join(', ');
        setExpiredRolesWarningMessage(
          expiredRoles.length > 0 ? t('UpdateExpiredRoleWarning').replace('{0}', expiredRoleString).replaceAll('{1}', username) : ''
        );
      }
    }
    setSaveRoleWarning(true);
  };
  const verifyIfUserAlreadyHasSelectedRole = (
    userRoles: { id: string; expirationDate?: Date; scopeId: string; assignmentId: string }[],
    selectedRoles: { value: string; label: string }[]
  ) => {
    var existingRoles = [];
    for (const selectedRole of selectedRoles) {
      const userRole = userRoles.find((v) => v.id === selectedRole.value);
      if (userRole) {
        existingRoles.push({
          id: userRole.id,
          expirationDate: userRole.expirationDate,
          label: selectedRole.label,
          scopeId: userRole.scopeId,
          assignmentId: userRole.assignmentId,
        });
      }
    }
    return existingRoles;
  };

  const assignRoles = async () => {
    let item = (await form.validateFields()) as UserRoleAssignmentFormData;
    const newRoles = item.roles.filter((v) => !expiredRoleAssignments.current.some((x) => x.roleId === v.value));

    let isSuccess = true;
    if (newRoles.length > 0) {
      const postData: CreateRoleAssignmentItem = {
        identityId: userId,
        identityType: IdentityType.User,
        roles: newRoles.map((r) => ({ roleId: r.value, role: r.label, expirationDate: roleAttributes?.expirationDate })),
        scopeId: item.hierarchy.scopeId,
      };

      isSuccess = isSuccess && (await newRoleAssignmentStore.create(postData))?.isSuccess === true;

      if (isSuccess && !expiredRoleAssignments.current.some((v) => v.label === LicenseManagerRoleName)) {
        await createLicenseAttributes(userId, postData.scopeId);
      }
    }

    if (expiredRoleAssignments.current.length > 0 && roleAttributes?.expirationDate) {
      const response = await roleAssignmentSummaryStore.updateExpirationInBulk(
        expiredRoleAssignments.current.map((v) => ({
          assignmentId: v.assignmentId,
          scopeId: v.scopeId,
          newExpirationDate: roleAttributes.expirationDate!,
        }))
      );
      isSuccess = isSuccess && response?.isSuccess === true;
      const existingLicenseManagerRole = expiredRoleAssignments.current.find((v) => v.label === LicenseManagerRoleName);
      if (isSuccess && existingLicenseManagerRole) {
        await createLicenseAttributes(userId, existingLicenseManagerRole.scopeId);
      }
    }

    expiredRoleAssignments.current = [];
    form.resetFields();
    handleClose({ isSuccess });
  };

  const assignRolesAndAddToOrganization = async () => {
    let item = (await form.validateFields()) as UserRoleAssignmentFormData;

    if (!selectedOrg) return;

    let response = await organizationStore.addOrganizationMembers(selectedOrg, [
      {
        displayName: username,
        email: identity.email ?? '',
        userId: userId,
        resendInvitation: false,
        roles: item.roles.map((v) => ({ expirationDate: roleAttributes?.expirationDate, role: v.label, roleId: v.value })),
      },
    ]);

    const postData: CreateRoleAssignmentItem = {
      identityId: userId,
      identityType: IdentityType.User,
      roles: item.roles.map((r) => ({ roleId: r.value, role: r.label, expirationDate: roleAttributes?.expirationDate })),
      scopeId: item.hierarchy.scopeId,
    };

    await userStore.load(userId);

    if (response?.isSuccess) {
      createLicenseAttributes(userId, postData.scopeId).then(() => {
        form.resetFields();
        handleClose(response);
      });
    }
  };

  const onNewItem = async () => {
    if (selectedOrg && userOrganizations?.includes(selectedOrg)) {
      assignRoles();
    } else {
      assignRolesAndAddToOrganization();
    }
  };

  const createLicenseAttributes = async (userId: string, scopeId: string) => {
    if (licenseAttributes) {
      let item: NewLicensCreationAttributes = {
        identityType: IdentityType.User,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId: userId,
        scopeId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }

      const expiredLicenseManagerRole = expiredRoleAssignments.current.find((v) => v.label === LicenseManagerRoleName);
      if (expiredLicenseManagerRole) {
        await licenseCreationAttributesStore.getByUserScope({
          identityId: userId,
          identityType: IdentityType.User,
          scopeId: expiredLicenseManagerRole.scopeId,
        });

        if (licenseCreationAttributesStoreState.item.value) {
          await licenseCreationAttributesStore.save(licenseCreationAttributesStoreState.item.value.id, {
            ...licenseCreationAttributesStoreState.item.value,
            ...item,
          });
          return;
        }
      }

      await licenseCreationAttributesStore.post(item);
    }
  };
  const onRolesChange = async (values: { value: string; label: string; selector: Selector }[]) => {
    var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
    if (!licenseAttributesVisible) {
      licenseMnagerRoleSelected && setLicenseAttributesVisible(true);
    } else {
      !licenseMnagerRoleSelected && setLicenseAttributesVisible(false);
    }
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributes(item);
  };

  const handleClose = (response?: any) => {
    newRoleAssignmentStore.clearMessages();
    roleAssignmentSummaryStore.clearMessages();
    licenseCreationAttributesStore.clearMessages();
    onClose(response, addAnother);
  };

  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('AssignRolesToUser')}
      onNewItem={getWarningMessage}
      onClose={handleClose}
      onOkButtonIsDisabled={
        newRoleAssignmentState.isBusy.get() ||
        licenseCreationAttributesStoreState.isBusy.get() ||
        organizationState?.item?.value?.members?.length >= ORGANIZATIONS_MEMBER_LIMIT
      }
      onCancelButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get()}
      closable={!newRoleAssignmentState.isBusy.get()}
      maskClosable={!newRoleAssignmentState.isBusy.get()}
      okText={t('Confirm')}
      errorMessage={newRoleAssignmentState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin
            spinning={
              newRoleAssignmentState.isBusy.get() ||
              checkingPermissions === true ||
              licenseCreationAttributesStoreState.isBusy.get() ||
              roleAssignmentSummaryState.isBusy.get() ||
              rolesState.isBusy.get() ||
              organizationState.isBusy.get() ||
              userState.isBusy.get()
            }
          >
            {selectedOrg &&
              !userOrganizations?.includes(selectedOrg) &&
              organizationState?.item?.value?.members?.length < ORGANIZATIONS_MEMBER_LIMIT && (
                <Alert
                  type="warning"
                  message={t('UserWillBeAddedToOrganizationBeforeAssign').replaceAll('{0}', username).replace('{1}', organizationName)}
                />
              )}

            {selectedOrg &&
              !userOrganizations?.includes(selectedOrg) &&
              organizationState?.item?.value?.members?.length >= ORGANIZATIONS_MEMBER_LIMIT && (
                <Alert type="error" message={t('OrganizationReachedUserLimit').replaceAll('{0}', username).replace('{1}', organizationName)} />
              )}
            <Form size="middle" form={form} layout="vertical" autoComplete="off" initialValues={{ roles: [] }}>
              {!checkingPermissions && (
                <Form.Item
                  name={nameof<UserRoleAssignmentFormData>('hierarchy')}
                  rules={[
                    () => ({
                      required: true,
                      validator(_, value) {
                        if ((value?.scopeId || '').length === 0) return Promise.reject(t('PleaseSelect'));
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <ScopeHierarchySelector
                    onChange={async (x) => {
                      const orgId = (x?.path || []).length < 2 ? undefined : x.path[1];
                      setSelectedOrg(orgId);
                      form.setFieldsValue({ ...form.getFieldsValue(), roles: [] });
                      if (x?.idx === 0) {
                        setOrganizationName(x?.scopeTitle);
                      }
                      let currentSelector: Selector;
                      if (x.lastScope === ScopeType.Organization) {
                        if (x.scopeTitle.toLowerCase() === 'sptab') {
                          setIsSptAbSelected(true);
                        } else {
                          setIsSptAbSelected(false);
                        }
                        currentSelector = await getOrganizationSelector(orgId);
                      } else {
                        currentSelector = getSelectorFromScope(x.lastScope ?? ScopeType.Organization);
                      }
                      setCurrentMaxSelector(currentSelector);
                    }}
                    allowedScopeRoots={userOrganizations}
                    partialSelectionMode={canAddUserToOrganization}
                  />
                </Form.Item>
              )}

              <Form.Item name={nameof<UserRoleAssignmentFormData>('roles')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                <RolesSelect
                  maxSelector={currentMaxSelector}
                  organizationId={selectedOrg}
                  onChange={onRolesChange}
                  excludeNames={[
                    !hasGlobalLicenseManagerPermission ? LicenseManagerRoleName : '',
                    !hasGlobalLicenseManagerPermission ? LicenseApproverRoleName : '',
                    !sptAbIsSelected ? GlobalLicenseManagerRoleName : '',
                    StructmasterUserRoleName,
                  ]}
                />
              </Form.Item>
              <Text style={{ marginBottom: 10 }} strong>
                {t('RoleAssignmentAttributes')}:
              </Text>
              <RoleAssignmentAttributesComponent onChange={onRoleAttributesChange} />
              {licenseAttributesVisible && (
                <>
                  <Divider />
                  <Text style={{ marginBottom: 10 }} strong>
                    {t('LicenseManagerRoleAttributes')}:
                  </Text>
                  <LicenseAttributesComponent onChange={onLicenseAttributesChange} />
                </>
              )}
            </Form>

            <Checkbox value={addAnother} onChange={(v) => setAddAnother(v.target.checked)} style={{ marginBottom: 20 }}>
              {t('AddAnotherAssignment')}
            </Checkbox>
          </Spin>
          {saveRoleWarning && (warningMessage || expiredRolesWarningMessage) && (
            <Modal
              title={<h3>{`${t('Confirmation')}`}</h3>}
              open={saveRoleWarning}
              onOk={() => {
                setSaveRoleWarning(false);
                onNewItem();
              }}
              onCancel={() => handleClose()}
              okText={t('OK')}
              okButtonProps={{
                style: { width: '45%', float: 'right' },
                disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
              cancelText={t('Cancel')}
              cancelButtonProps={{
                type: 'default',
                style: { width: '45%' },
                disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
            >
              <Spin
                spinning={newRoleAssignmentState.isBusy.get() || checkingPermissions === true || licenseCreationAttributesStoreState.isBusy.get()}
              >
                {warningMessage !== '' && <p>{warningMessage}</p>}
                {expiredRolesWarningMessage !== '' && <p>{expiredRolesWarningMessage}</p>}
                <p>{t('DoYouWantToContinue')}</p>
              </Spin>
            </Modal>
          )}
        </>
      }
    />
  );
};

export default UserRoleAssignmentModal;
